import { getApiBase } from '../apiBase.js';
import { beginInboxMutation } from './appUpdates.js';

export function createInboxApi(signal, onSessionChange) {
  const sessionToken = localStorage.getItem('jwtToken');
  function checkSession() {
    if (signal?.aborted) throw new DOMException('Inbox session ended', 'AbortError');
    const token = localStorage.getItem('jwtToken');
    if (token !== sessionToken && onSessionChange) {
      onSessionChange();
      // The caller belongs to the previous credential. Verification must not
      // silently continue its mutation with the replacement credential.
      throw new DOMException('Inbox session changed', 'AbortError');
    }
    if (!token || token !== sessionToken) throw Object.assign(new Error('Authentication required'), { status: 401 });
  }
  return async function request(path, { method = 'GET', body, timeoutMs } = {}) {
    checkSession();
    const finishMutation = ['GET', 'HEAD'].includes(method.toUpperCase()) ? () => {} : beginInboxMutation();
    const bounded = Number.isFinite(timeoutMs) && timeoutMs > 0 ? new AbortController() : null;
    const cancel = () => bounded?.abort();
    const timer = bounded ? setTimeout(cancel, timeoutMs) : null;
    if (bounded) signal?.addEventListener('abort', cancel, { once: true });
    const requestSignal = bounded?.signal || signal;
    try {
      const response = await fetch(`${getApiBase()}/studentplayground/api/inbox/${path}`, {
        method, signal: requestSignal, cache: 'no-store', credentials: 'same-origin',
        headers: { Authorization: `Bearer ${sessionToken}`, ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}) },
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      });
      let data;
      try { data = await response.json(); } catch { data = {}; }
      if (requestSignal?.aborted) throw new DOMException('Inbox request aborted', 'AbortError');
      checkSession();
      if (!response.ok) throw Object.assign(new Error('Inbox request failed'), { status: response.status, code: data.code });
      return data;
    } catch (problem) { checkSession(); throw problem; }
    finally {
      finishMutation();
      clearTimeout(timer);
      if (bounded) signal?.removeEventListener('abort', cancel);
    }
  };
}

export function errorText(error) {
  const codes = {
    inbox_update_in_progress: 'Vyksta saugaus atnaujinimo patikra. Užklausa nebuvo siųsta; palaukite, kol patikra baigsis.',
    inbox_client_update_required: 'Atnaujinkite pašto dėžutės versiją prieš tęsdami. Neišsaugotą juodraštį pirmiausia nukopijuokite.',
    history_range_reload_required: 'Nepavyko saugiai atkurti ankstesnės istorijos vietos. Atverkite naujausią istoriją; juodraštis liks šiame lange ir nebus siunčiamas.',
    conversation_unavailable: 'Pokalbio nuoroda neteisinga arba pokalbis nepasiekiamas. Pasirinkite pokalbį iš sąrašo.',
    template_required: 'Atsakymo langas uždarytas. Pasirinkite patvirtintą šabloną.',
    template_preview_conflict: 'Šablonas pasikeitė. Atnaujinkite pokalbį ir peržiūrėkite šabloną dar kartą.',
    template_not_approved: 'Šis šablonas nėra patvirtintas.',
    template_unverified: 'Šio šablono patvirtinimas dar nepatikrintas.',
    template_verification_stale: 'Patvirtinimo patikra pasenusi. Reikia naujos tiekėjo patikros.',
    viber_template_parameter_too_long: 'Viber šablono parametro reikšmė negali viršyti 125 simbolių. Sutrumpinkite ją ir peržiūrėkite žinutę dar kartą.',
    viber_template_url_requires_fixed_domain: 'Nuorodos domenas turi būti įrašytas patvirtintame šablone. Kreipkitės į IT dėl šablono pakeitimo.',
    provider_configuration_invalid: 'Patikrinkite tiekėjo prieigos teises ir paskyros nustatymus. Kreipkitės į IT.',
    draft_conflict: 'Juodraštis pakeistas kitame įrenginyje. Jūsų tekstas išsaugotas šiame lange; prieš atnaujindami jį nukopijuokite.',
    draft_not_saved: 'Juodraštis dar neišsaugotas serveryje. Tekstas liko šiame lange. Atkurkite ryšį arba nukopijuokite tekstą prieš išeidami.',
    send_outcome_unknown: 'Nežinome, ar užklausa priimta. Patikrinkite ankstesnės užklausos būseną; nekopijuokite jos į naują siuntimą.',
    send_reconciliation_required: 'Pirmiausia patikrinkite ankstesnę užklausą. Pakeisto teksto negalima siųsti kaip neaiškaus siuntimo pakaitalo.',
    command_expired: 'Paruošta užklausa nebegalioja. Patikrinkite jos būseną, tada peržiūrėkite juodraštį prieš naują siuntimą.',
    command_cancelled: 'Paruošta užklausa atšaukta. Patikrinkite jos būseną prieš kurdami naują siuntimą.',
    command_binding_conflict: 'Užklausos siuntėjas arba gavėjas pasikeitė. Atšaukite seną užklausą ir kreipkitės į IT dėl patikros.',
    command_pending_conflict: 'Pirmiausia patikrinkite arba atšaukite ankstesnę paruoštą užklausą. Nekurkite jos kopijos.',
    command_limit: 'Per daug paruoštų užklausų. Pirmiausia patikrinkite arba atšaukite ankstesnes užklausas.',
    recovery_storage_unavailable: 'Naršyklė negali išsaugoti siuntimo atkūrimo duomenų. Nesiųskite iš naujo kitame lange; kreipkitės į IT.',
    recovery_storage_full: 'Per daug nepatikrintų užklausų šiame įrenginyje. Pirmiausia patikrinkite ankstesnius siuntimus.',
    conversation_conflict: 'Pokalbį pakeitė kitas darbuotojas. Duomenys atnaujinti; patikrinkite ir pakartokite veiksmą.',
    notification_consent_required: 'Nėra galiojančio sutikimo gauti pranešimus.',
    notifications_opted_out: 'Gavėjas atsisakė pranešimų.',
    parent_identity_required: 'Pirmiausia reikia patvirtinti tėvo / mamos kontaktą.',
    parent_identity_changed: 'Tėvo / mamos kontaktas pasikeitė. Reikia patvirtinti iš naujo.',
    sending_disabled: 'Žinučių siuntimas šiuo metu išjungtas.',
    channel_disabled: 'Šio kanalo siuntimas išjungtas.',
    recipient_not_allowed: 'Gavėjas neįtrauktas į bandomojo siuntimo sąrašą.',
    pilot_not_configured: 'Bandomojo siuntimo gavėjų sąrašas nesukonfigūruotas. Kreipkitės į IT.',
    sender_binding_changed: 'Siuntėjo nustatymai pasikeitė. Prieš tęsiant reikalinga IT patikra.',
    recipient_binding_changed: 'Gavėjo duomenys pasikeitė. Prieš tęsiant reikia patvirtinti kontaktą.',
    viber_burst_exhausted: 'Pasiektas Viber žinučių limitas. Palaukite kliento atsakymo.',
    viber_session_exhausted: 'Pasiektas šios Viber sesijos limitas. Palaukite naujos kliento pradėtos sesijos arba naudokite patvirtintą šabloną.',
    viber_session_unverified: 'Šios Viber sesijos limitų dar negalime patvirtinti. Reikalinga nauja sesija arba patvirtintas šablonas.',
    recovery_conflict: 'Siuntimo būsena pasikeitė. Peržiūrėkite atnaujintą būseną ir spręskite iš naujo.',
    delivery_order_conflict: 'Vėlesnė žinutė jau perduota siųsti. Senos užklausos paleisti nebegalima; peržiūrėkite ir patvirtinkite naują žinutę eilės pabaigoje arba sustabdykite bandymus.',
    replacement_confirmation_required: 'Prieš tęsdami patikrinkite žinutės turinį ir patvirtinkite naują siuntimą eilės pabaigoje.',
    message_content_purged: 'Žinutės turinys pašalintas pagal saugojimo taisykles. Pakartotinis siuntimas negalimas.',
    receipt_review_conflict: 'Įvykio būsena pasikeitė. Peržiūrėkite atnaujintą būseną ir patvirtinkite sprendimą iš naujo.',
    receipt_replay_unavailable: 'Šio įvykio dabar negalima apdoroti pakartotinai. Patikrinkite jo būseną.',
    receipt_content_purged: 'Įvykio turinys pašalintas. Pakartotinis apdorojimas negalimas.',
    history_source_missing: 'Istorijos šaltinio įrašo nebėra. Pakartojimas jo neatkurs; kreipkitės į IT dėl istorijos perkėlimo patikros.',
    receipt_route_repair_invalid: 'Paskyros susiejimo negalima patvirtinti. Patikrinkite siuntėjo nustatymus ir pasirinkite patikrintą paskyrą iš naujo.',
    duplicate_risk_confirmation_required: 'Prieš kurdami naują siuntimą patvirtinkite galimos kopijos riziką.',
    recovery_action_invalid: 'Šis veiksmas nebegalimas. Patikrinkite dabartinę siuntimo būseną.',
    inbox_disabled: 'Bendra pašto dėžutė dar neįjungta.',
    idempotency_conflict: 'Siuntimo užklausa pasikeitė. Patikrinkite pokalbį prieš siųsdami dar kartą.',
  };
  if (error.status === 401) return 'Prisijungimo sesija baigėsi. Prisijunkite dar kartą.';
  if (error.status === 403) return 'Neturite prieigos prie bendros pašto dėžutės.';
  return codes[error.code] || 'Nepavyko atlikti veiksmo. Patikrinkite ryšį ir juodraščio išsaugojimo būseną.';
}

export const parameterLabels = {
  parent_name: 'Tėvo / mamos vardas', class_subject: 'Pamokos dalykas', class_date: 'Pamokos data',
  class_time: 'Pamokos laikas', old_date: 'Ankstesnė data', old_time: 'Ankstesnis laikas',
  new_date: 'Nauja data', new_time: 'Naujas laikas', amount: 'Suma / balansas', payment_link: 'Mokėjimo nuoroda',
};

export const deliveryLabels = {
  queued: 'Eilėje', pending: 'Eilėje', sending: 'Siunčiama', accepted: 'Priimta siųsti', sent: 'Išsiųsta',
  delivered: 'Pristatyta', read: 'Perskaityta', failed: 'Nepavyko pristatyti', unknown: 'Būsena neaiški — nekartokite siuntimo',
  blocked: 'Siuntimas sustabdytas', received: 'Gauta', internal: 'Tik darbuotojams',
  cancelled: 'Tolesni bandymai sustabdyti',
};
